import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { Box, Stack } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import DrawerContainer from 'components/drawer/common/DrawerContainer'
import DrawerHeader from 'components/drawer/common/DrawerHeader'
import FormInput from 'components/form-elements/FormInput'
import FormMultipleSelect from 'components/form-elements/FormMultipleSelect'
import { defaultDrawerSX, EMAIL_IS_INVALID, FIELD_IS_REQUIRED, PHONE_IS_INVALID } from 'const'
import React, { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { TeamMember, traineeAllRoles } from 'types'
import { array, literal, object, string, TypeOf } from 'zod'

export function TimelineTeamMemberDrawer({
  isOpen,
  onClose,
  teamMember,
}: {
  isOpen: boolean
  teamMember: TeamMember
  onClose: () => void
}) {
  const formSchema = object({
    firstName: string().min(1, FIELD_IS_REQUIRED),
    lastName: string().min(1, FIELD_IS_REQUIRED),
    email: string().min(1, FIELD_IS_REQUIRED).email(EMAIL_IS_INVALID),
    phone: string().min(10, PHONE_IS_INVALID).or(literal('')).optional(),
    role: array(
      object({
        label: string(),
        value: string(),
      }),
    ).optional(),
    otherRole: string().optional(),
  })

  type FormType = TypeOf<typeof formSchema>

  const defaultRoles = useMemo(() => {
    if (!teamMember.role) return []
    return teamMember.role.map((role) => {
      const option = traineeAllRoles.find((option) => option.value === role)
      return (
        option || {
          value: role,
          label: role,
        }
      )
    })
  }, [teamMember])

  const formSettings = {
    mode: 'onChange' as const,
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: teamMember.firstName || '',
      lastName: teamMember.lastName || '',
      email: teamMember.email || '',
      role: defaultRoles,
      otherRole: teamMember.otherRole || '',
    },
  }

  const methods = useForm<FormType>(formSettings)

  const onSubmitHandler = () => {}

  const roleValue = methods.watch('role')
  const hasOtherRole = useMemo(() => {
    return roleValue?.some((role) => role.value === 'other')
  }, [roleValue])

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose} sx={defaultDrawerSX}>
      <DrawerContainer isOpen={isOpen} hasData loading={false} handleClose={onClose}>
        <DrawerHeader handleClose={onClose}>Care Team Profile</DrawerHeader>

        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
            width="100%"
            noValidate
          >
            <Stack spacing={1.5}>
              <FormInput label="First Name" name="firstName" fullWidth disabled />
              <FormInput label="Last Name" name="lastName" fullWidth disabled />
              <FormInput label="Email" name="email" fullWidth disabled />
              <FormInput label="Phone Number" name="phone" fullWidth disabled />

              <FormMultipleSelect
                name="role"
                label="Role"
                options={traineeAllRoles}
                defaultValue={[]}
                isLoading={false}
                disabled
              />

              {hasOtherRole && <FormInput name="otherRole" label="Other Role" fullWidth disabled />}
            </Stack>
          </Box>
        </FormProvider>
      </DrawerContainer>
    </Drawer>
  )
}
